import { useEffect, useState } from "react";
import axios from "axios";
import { X, Bell, Check } from "lucide-react";
import { API } from "../lib/helpers";
import { toast } from "sonner";

/**
 * Contextual, intent-based opt-in prompt.
 *
 * Shows as a subtle inline card (NOT a modal) only when:
 *   - user is authenticated
 *   - user has visited this category page >= 2 times in the last 7 days
 *   - user has not already subscribed to this category
 *   - user has not already dismissed the prompt for this category
 *
 * Once the user acts (Notify Me OR Dismiss), the prompt never shows again.
 *
 * Props:
 *   - category: "top-deals" | "best-deals-under-300k" | "top-multifamily-deals"
 *   - isAuthenticated: boolean
 *   - newDeals24h: integer — used to make copy contextual when available
 *                           ("3 new deals added today — want alerts...")
 *   - categoryLabel: human-readable name like "under-$300K" / "multifamily"
 */
export default function DealAlertPrompt({
  category,
  isAuthenticated,
  newDeals24h = 0,
  categoryLabel,
  visibleDealIds = null, // array of property IDs rendered on the page right now
}) {
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState("idle"); // idle | subscribing | subscribed | dismissed
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios
      .get(`${API}/deal-alerts/prompt-state`, { params: { category } })
      .then((r) => {
        setVisible(!!r.data?.should_show);
      })
      .catch(() => {});
  }, [category, isAuthenticated]);

  // Fire analytics once when the prompt actually becomes visible.
  useEffect(() => {
    if (visible && state === "idle") {
      axios
        .post(`${API}/analytics/track`, {
          event_type: "deal_alerts_prompt_shown",
          metadata: { feature: "deal_alerts_prompt", category },
        })
        .catch(() => {});
    }
    // Intentionally NOT depending on `state` — we only want the first transition
    // from hidden -> visible to log, not every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!isAuthenticated || !visible || state === "dismissed") return null;

  const trackAnalytics = (event) => {
    axios
      .post(`${API}/analytics/track`, {
        event_type: event,
        metadata: { feature: "deal_alerts_prompt", category },
      })
      .catch(() => {});
  };

  const handleSubscribe = async () => {
    if (state !== "idle") return;
    setState("subscribing");
    setError(null);
    try {
      await axios.post(`${API}/deal-alerts/subscribe`, {
        category,
        // Attribution: log which deals were visible when they subscribed.
        // The backend explicitly does NOT treat this as causal.
        visible_deal_ids: Array.isArray(visibleDealIds) ? visibleDealIds.slice(0, 50) : [],
      });
      trackAnalytics("deal_alerts_prompt_subscribed");
      setState("subscribed");
      toast.success("You'll get an email when new deals are added.", {
        duration: 4000,
      });
    } catch (e) {
      setState("idle");
      setError("Couldn't enable alerts. Try again.");
    }
  };

  const handleDismiss = async () => {
    setState("dismissed");
    trackAnalytics("deal_alerts_prompt_dismissed");
    try {
      await axios.post(`${API}/deal-alerts/dismiss`, { category });
    } catch (e) {
      // Silent fail — local dismissed state already hides it
    }
  };

  const label = categoryLabel || "";
  const contextualPrefix =
    newDeals24h > 0
      ? `${newDeals24h} new ${label ? label + " " : ""}deal${newDeals24h === 1 ? "" : "s"} added today — `
      : "";

  // Success state: friendly confirmation instead of disappearing
  if (state === "subscribed") {
    return (
      <div
        className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 flex items-center gap-3"
        data-testid="deal-alert-prompt-confirmation"
      >
        <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
          <Check className="w-4 h-4 text-emerald-400" />
        </div>
        <div className="text-sm">
          <p className="font-medium text-foreground">
            You'll get an email when new deals are added.
          </p>
          <p className="text-xs text-muted-foreground">
            Max 1 per day. Unsubscribe any time.
          </p>
        </div>
      </div>
    );
  }

  // Main prompt
  return (
    <div
      className="rounded-lg border border-border bg-card p-4 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row"
      data-testid={`deal-alert-prompt-${category}`}
      role="region"
      aria-label="Deal alerts opt-in"
    >
      <div className="flex items-start gap-3 flex-1">
        <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Bell className="w-4 h-4 text-emerald-400" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {contextualPrefix}
            Get an email when new {label ? label + " " : ""}deals drop
            <span className="text-muted-foreground font-normal"> (max 1/day)</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Never miss new cash-flowing deals.
          </p>
          {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 w-full sm:w-auto">
        <button
          type="button"
          onClick={handleSubscribe}
          disabled={state === "subscribing"}
          data-testid={`deal-alert-prompt-notify-${category}`}
          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {state === "subscribing" ? "Enabling..." : "Notify Me"}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss prompt"
          data-testid={`deal-alert-prompt-dismiss-${category}`}
          className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
